/**
 * VoiceInput — microphone button that records audio and sends it to the
 * server-side STT endpoint (POST /api/v1/transcribe) for transcription.
 *
 * How it works:
 * - Click to record: getUserMedia({ audio: true }) → MediaRecorder collects
 *   chunks. A m:ss timer runs; recording auto-stops at MAX_SECONDS (180s).
 * - Click stop (or cap hit) → MediaRecorder.stop(). onstop builds a Blob,
 *   releases the mic, then POSTs the blob to the server.
 * - On success → the punctuated transcript is handed to the parent via
 *   onTranscript (parent owns append/spacing).
 * - Errors (no mic permission, getUserMedia/MediaRecorder unavailable, or a
 *   failed POST) surface as an inline caption; the button always returns to
 *   idle (never wedged).
 *
 * Replaces the previous browser Web Speech API implementation: server STT is
 * cross-browser (any browser with MediaRecorder), punctuates, and keeps the
 * speech model server-side. Trade-off: no live interim text — the user sees a
 * "Transcribing…" state after they stop instead of words appearing live.
 *
 * Browser support: needs navigator.mediaDevices.getUserMedia + MediaRecorder
 * (Chrome, Edge, Firefox, Safari 14.1+). Unsupported → disabled with tooltip.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import { useTranslation } from "react-i18next";

import { transcribeAudio } from "../api/client";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Hard cap on a single recording. Recording auto-stops here. */
const MAX_SECONDS = 180;

// ---------------------------------------------------------------------------
// Capability check
// ---------------------------------------------------------------------------

function isDictationSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window.MediaRecorder !== "undefined"
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

type Phase = "idle" | "recording" | "transcribing";

// ---------------------------------------------------------------------------
// Props — UNCHANGED contract (parent integration stays identical)
// ---------------------------------------------------------------------------

interface VoiceInputProps {
  /** Called with the transcribed text. Parent owns append/spacing. */
  onTranscript: (text: string) => void;
  /** Whether the parent input is disabled. */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function VoiceInput({ onTranscript, disabled }: VoiceInputProps) {
  const { t } = useTranslation("common");
  const [phase, setPhase] = useState<Phase>("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Supported is computed once on mount (it never changes mid-session).
  const [supported] = useState(isDictationSupported);

  // Refs hold the live recording machinery so cleanup can always reach it,
  // independent of render timing.
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Tracks mounted state so the async onstop handler never calls setState (or
  // the parent's onTranscript) after the component has unmounted mid-upload.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // -------------------------------------------------------------------------
  // Cleanup helpers
  // -------------------------------------------------------------------------

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // -------------------------------------------------------------------------
  // Stop recording — triggers MediaRecorder.onstop, which does the upload.
  // -------------------------------------------------------------------------

  const stopRecording = useCallback(() => {
    clearTimer();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop(); // fires onstop → blob build + transcribe
    }
  }, [clearTimer]);

  // -------------------------------------------------------------------------
  // Start recording
  // -------------------------------------------------------------------------

  const startRecording = useCallback(async () => {
    setError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      // Permission denied, no device, or insecure context.
      setError(t("VoiceInput.micDenied"));
      setPhase("idle");
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream);
    } catch {
      releaseStream();
      setError(t("VoiceInput.startError"));
      setPhase("idle");
      return;
    }
    recorderRef.current = recorder;

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onstop = async () => {
      clearTimer();
      const mimeType = recorder.mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: mimeType });
      chunksRef.current = [];
      releaseStream(); // release the mic ASAP

      // Bailed out via unmount cleanup → don't touch dead component state.
      if (!mountedRef.current) return;

      // No audio captured (e.g. instant stop) → quietly return to idle.
      if (blob.size === 0) {
        setPhase("idle");
        return;
      }

      setPhase("transcribing");
      try {
        const result = await transcribeAudio(blob);
        // Component may have unmounted during the in-flight upload — guard
        // the success path so onTranscript + setState don't run post-unmount.
        if (!mountedRef.current) return;
        const text = (result.text || "").trim();
        if (text) {
          onTranscript(text);
        }
        setPhase("idle");
      } catch {
        // 403 paid-gate / 413 too-long / 429 rate-limit / 502 / network.
        // Same guard on the failure path.
        if (!mountedRef.current) return;
        setError(t("VoiceInput.transcribeError"));
        setPhase("idle");
      }
    };

    setElapsed(0);
    setPhase("recording");
    recorder.start();

    // m:ss timer — ONLY increments elapsed. The auto-stop at the cap is
    // handled by the effect below (watching `elapsed`), so we never call
    // stopRecording from inside a state updater (which could race a manual
    // stop and double-fire).
    timerRef.current = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
  }, [clearTimer, releaseStream, onTranscript, t]);

  // -------------------------------------------------------------------------
  // Auto-stop at the cap — driven off `elapsed` rather than from inside the
  // interval's setElapsed updater. stopRecording is stable (useCallback) and
  // idempotent (guards on recorder.state !== "inactive"), so this won't
  // double-stop a manual stop.
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (phase === "recording" && elapsed >= MAX_SECONDS) {
      stopRecording();
    }
  }, [elapsed, phase, stopRecording]);

  // -------------------------------------------------------------------------
  // Unmount cleanup — never leak a live mic or interval.
  // -------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      clearTimer();
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        // Drop the handler so the in-flight upload doesn't fire post-unmount.
        recorder.onstop = null;
        recorder.stop();
      }
      releaseStream();
    };
  }, [clearTimer, releaseStream]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // Unsupported browser → disabled control with an explanatory tooltip.
  if (!supported) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title={t("VoiceInput.unsupported")}>
          <span>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<MicIcon />}
              disabled
              size="small"
            >
              {t("VoiceInput.dictate")}
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  }

  const isRecording = phase === "recording";
  const isTranscribing = phase === "transcribing";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Tooltip
        title={
          isRecording
            ? t("VoiceInput.stopTooltip")
            : isTranscribing
            ? t("VoiceInput.transcribingTooltip")
            : t("VoiceInput.dictateTooltip")
        }
      >
        <span>
          <Button
            variant={isRecording ? "contained" : "outlined"}
            color={isRecording ? "error" : "primary"}
            startIcon={
              isTranscribing ? (
                <CircularProgress size={16} color="inherit" />
              ) : isRecording ? (
                <StopIcon />
              ) : (
                <MicIcon />
              )
            }
            onClick={isRecording ? stopRecording : startRecording}
            disabled={disabled || isTranscribing}
            size="small"
            sx={
              isRecording
                ? {
                    animation: "pulse 1.5s infinite",
                    "@keyframes pulse": {
                      "0%": { boxShadow: "0 0 0 0 rgba(244,67,54,0.4)" },
                      "70%": { boxShadow: "0 0 0 10px rgba(244,67,54,0)" },
                      "100%": { boxShadow: "0 0 0 0 rgba(244,67,54,0)" },
                    },
                  }
                : {}
            }
          >
            {isRecording
              ? t("VoiceInput.stop")
              : isTranscribing
              ? t("VoiceInput.transcribing")
              : t("VoiceInput.dictate")}
          </Button>
        </span>
      </Tooltip>

      {isRecording && (
        <Typography
          variant="caption"
          color="error"
          sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}
        >
          {formatTime(elapsed)}
        </Typography>
      )}

      {error && (
        <Typography variant="caption" color="error.main">
          {error}
        </Typography>
      )}
    </Box>
  );
}
